import api from 'controllers/raymanAPI.js';
import WebRTCInterface from './WebRTCInterface.js';
import EventsDispatcher from 'controllers/EventsDispatcher.js';
import Singletons from 'controllers/Singletons.js';

enum VideoCallStatus
{
    CALLING,
    INCOMING,
    ACCEPTED,
    REJECTED,
    ENDED,
    STARTING,
    CONNECTING,
    LIVE,
    ERROR
}


enum CallMessages
{
    call = 'operations.calls.start',
    accept = 'operations.calls.accept',
    end = 'operations.calls.end',
    connected = 'operations.calls.connected'
}


enum CallEvents
{
    statusChanged = "statusChanged",
    callStarted = "callStarted",
    callEnded = "callEnded",
    callIncoming = "callIncoming",
    mediaChanged = "mediaChanged",
    callOperationShowing = "callOperationShowing",
    callOperationHidden = "callOperationHidden",
    callError = "callError",
    highlightReceived = "highlightReceived",
    highlightEnabled = "highlightEnabled",
}

enum CallEndCauses
{
    userEnded = 'user_ended',
    rejected = 'user_rejected',
    p2pInitFailed = 'p2p_init_failed',
    p2pConnectionClosed = 'p2p_connection_closed'
}

enum CallEndStatus
{
    ended = 'ended',
    rejected = 'rejected',
    error = 'error',
    missed = 'missed'
}

enum CameraFacingMode
{
    user = 'user',
    environment = 'environment',
    unknown = 'unknwown'
}

interface PingCoords {x: number, y: number}

interface CameraConfig {useCamera: boolean, facingMode: CameraFacingMode}

interface CallAcceptMessage {
    callID: string,
    configuration: CameraConfig
}

interface CallStartMessage extends CallAcceptMessage{
    operationID: number,
    caller: number,
    callee: number
}

interface CallEndMessage {
    callID: string,
    status: CallEndStatus
}


class CallsManager extends EventsDispatcher
{
    CurrentCall: Videocall | null;
    CurrentCallOperation: number | null;
    videoTargets: {local: null, remote: null}
    sounds: {
        hangup: HTMLAudioElement,
        calling: HTMLAudioElement,
        rejected: HTMLAudioElement
    }
    constructor()
    {
        super();
        this.CurrentCall = null;
        this.CurrentCallOperation = null;
        this.videoTargets = {local: null, remote: null};
        this.sounds = {
            hangup: new Audio(`${process.env.PUBLIC_URL}/sound/rayman_hangup.mp3`),
            calling: new Audio(`${process.env.PUBLIC_URL}/sound/rayman_calling.mp3`),
            rejected: new Audio(`${process.env.PUBLIC_URL}/sound/rayman_rejected.mp3`)
        }
        
        // initialize sounds
        this.sounds.calling.loop = true;

        if(Singletons.initialized)
        {
            this._suscribeToNotifications();
        }
        else
        {
            Singletons.on("userLoggedIn", () => this._suscribeToNotifications());
        }
    }

    get remoteVideoElement()
    {
        return this.CurrentCall ? this.CurrentCall.remoteVideoElement : null; 
    }

    get localVideoElement()
    {
        return this.CurrentCall ? this.CurrentCall.localVideoElement : null; 
    }

    get isInCall()
    {
        return this.CurrentCall !== null && this.CurrentCall.Status !== VideoCallStatus.ENDED;
    }

    _suscribeToNotifications()
    {
        console.log("Suscribing to call notifications...");
        Singletons.NotificationsSocket?.on('operations.calls.started', (message : CallStartMessage) => this._handleIncomingCall(message) );
        Singletons.NotificationsSocket?.on('operations.calls.ended', (message : CallEndMessage) => this._handleEndedCall(message) );
        Singletons.NotificationsSocket?.on('operations.calls.accepted', (message : CallStartMessage) => this._handleAcceptedCall(message) );
    }

    _SendMessage(topic : CallMessages, content : object)
    {
        Singletons.NotificationsSocket?.Send(topic, 'module', content);
    }

    _handleIncomingCall(message : CallStartMessage)
    {
        console.debug("Llamada entrante...");
        if(!this.CurrentCall || this.CurrentCall.Status === VideoCallStatus.ENDED)
        {
            console.debug("Creando Llamada entrante...");
            // Create new call object
            this._CreateReceivingCall(message);
        }
        else
        {
            // Auto reject call, we are in another call
            this._SendMessage(CallMessages.end, {callID: message.callID, cause: CallEndCauses.rejected});
        }
    }

    _handleAcceptedCall(message: CallStartMessage)
    {
        if(!this.CurrentCall || message.callID !== this.CurrentCall.callID) return;

        this.sounds.calling.pause();
        this.sounds.calling.currentTime = 0;
        this.CurrentCall.highlightEnabled = message.configuration.useCamera && message.configuration.facingMode === CameraFacingMode.environment;
    }
    
    _handleEndedCall(message: CallEndMessage)
    {
        if(!this.CurrentCall || message.callID !== this.CurrentCall.callID) return;

        this._CallEnded(message.status);
    }


    async Call(calleeID : number, operationID : number, video = true)
    {
        if(this.CurrentCall != null && this.CurrentCall.Status !== VideoCallStatus.ENDED) return;
        
        this.CurrentCallOperation = operationID;
        this.dispatch(CallEvents.callStarted, operationID);
        let hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(api.internalApi.token));
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        // encode to hex string
        let callID = hashArray.map(b => ('00' + b.toString(16)).slice(-2)).join('');
        this.CurrentCall = new Videocall(callID, Singletons.currentUser.userID, calleeID, this, VideoCallStatus.CALLING, video, false);
        this.CurrentCallOperation = operationID;

        let callMessage : CallStartMessage = {
            callID: callID,
            callee: calleeID,
            caller: Singletons.currentUser.userID,
            operationID: operationID,
            configuration: {
                useCamera: video,
                facingMode: CameraFacingMode.user
            }
        }
        this._SendMessage(CallMessages.call, callMessage);

        this.sounds.calling.play();
    }

    AcceptCall(video = true)
    {
        this.CurrentCall?.AcceptCall(video);
        this.sounds.calling.pause();
        this.sounds.calling.currentTime = 0;
        this.dispatch(CallEvents.callStarted, this.CurrentCallOperation);
    }

    RejectCall()
    {
        this.CurrentCall?.RejectCall();
    }

    EndCall()
    {
        this.CurrentCall?.EndCall();
    }

    _CreateReceivingCall(startMsg : CallStartMessage)
    {
        this.CurrentCallOperation = startMsg.operationID;
        this.CurrentCall = new Videocall(startMsg.callID, 
            startMsg.caller, startMsg.callee, this, VideoCallStatus.INCOMING, false, 
            startMsg.configuration.useCamera && startMsg.configuration.facingMode === CameraFacingMode.environment);
        
        this.dispatch(CallEvents.callIncoming, {operationID: startMsg.operationID, caller: startMsg.caller});
        this.sounds.calling.play();
    }

    _CallStatusChanged(newStatus : VideoCallStatus)
    {
        this.dispatch(CallEvents.statusChanged, newStatus);
        if(newStatus === VideoCallStatus.ENDED)
        {
            this._CallEnded(CallEndStatus.ended);
        }
        
        console.warn("call status changed to: "+VideoCallStatus[newStatus]);

    }

    _CallEnded(cause : CallEndStatus | null)
    {
        if(this.CurrentCall === null) return;
        if(this.CurrentCallOperation === null) return;

        this.dispatch(CallEvents.callEnded, this.CurrentCallOperation);
        this.CurrentCall.Dispose();
        this.CurrentCallOperation = null;
        this.CurrentCall = null;

        switch(cause)
        {
            case CallEndStatus.rejected:
                this.sounds.rejected.play();
            break;
            default:
                this.sounds.hangup.play();
                break;
        }

        this.sounds.calling.pause();
        this.sounds.calling.currentTime = 0;
    }

    SendHighlight(coords : PingCoords)
    {
        if(this.CurrentCall === null || this.CurrentCall.Status !== VideoCallStatus.LIVE) return;
        this.CurrentCall.SendHighlight(coords);
    }
}


class Videocall
{
    callID: string;
    caller: number;
    callee: number;
    __status: VideoCallStatus;
    callManager: CallsManager;
    webRtcInstance: WebRTCInterface;
    enableCam: boolean;
    _highlightEnabled: boolean;

    constructor(callID : string, caller : number, callee : number, callManager : CallsManager, initialStatus : VideoCallStatus, enableCam : boolean, enableHighlight: boolean)
    {
        this.callManager = callManager;
        this.callID = callID;
        this.caller = caller;
        this.callee = callee;
        this.__status = initialStatus;
        this.webRtcInstance = new WebRTCInterface();
        this.Status = initialStatus;
        this.enableCam = enableCam;
        this._highlightEnabled = enableHighlight;

        this.webRtcInstance.on("mediaChanged", this._handleMediaChanged);
        this.webRtcInstance.on("callEnded", this._handleCallEnded );
        this.webRtcInstance.on("error", this._handleError);
        this.webRtcInstance.on("connectionStarted", this._handleConnectionStarted);
        this.webRtcInstance.on("dataMessageReceived", this._handleDataMessageReceived);

        if(initialStatus === VideoCallStatus.CALLING)
        {
            this.webRtcInstance.Start(this.callID, true, this.enableCam, true);
        }
    }

    _handleMediaChanged = () => this.callManager.dispatch(CallEvents.mediaChanged);
    _handleCallEnded = () => this.callManager._CallEnded(CallEndStatus.ended);
    _handleError = () => this.DeclareError();
    _handleConnectionStarted = () => {this.Status = VideoCallStatus.LIVE; };
    _handleDataMessageReceived = (message : BufferSource) => this._parseMessageData(message);

    get Status()
    {
        return this.__status;
    }

    get remoteVideoElement()
    {
        return this.webRtcInstance ? this.webRtcInstance.mRemoteVideo : null;
    }

    get localVideoElement()
    {
        return this.webRtcInstance ? this.webRtcInstance.mLocalVideo : null;
    }

    set highlightEnabled(value : boolean)
    {
        this._highlightEnabled = value;
        this.callManager.dispatch(CallEvents.highlightEnabled, value);
    }

    get highlightEnabled()
    {
        return this._highlightEnabled;
    }

    set Status(value)
    {
        //var prevStatus = this.__status;
        this.__status = value;
        this.callManager._CallStatusChanged(value);
        // eslint-disable-next-line default-case
        switch(value)
        {   
            case VideoCallStatus.ENDED:
                this.webRtcInstance.Stop();
                break;

            case VideoCallStatus.CONNECTING:
                this.webRtcInstance.Start(this.callID, true, this.enableCam, false);
                break;
            case VideoCallStatus.LIVE:
                this.callManager._SendMessage(CallMessages.connected, {callID: this.callID});
                break;
        }
        
    }

    Dispose()
    {
        this.webRtcInstance.Stop();
        this.webRtcInstance._Cleanup();
        this.webRtcInstance.off("mediaChanged", this._handleMediaChanged);
        this.webRtcInstance.off("callEnded", this._handleCallEnded );
        this.webRtcInstance.off("error", this._handleError);
        this.webRtcInstance.off("connectionStarted", this._handleConnectionStarted);
        this.webRtcInstance.off("dataMessageReceived", this._handleDataMessageReceived);
    }

    _parseMessageData(message : BufferSource)
    {
        let decoder = new TextDecoder('ascii');
        let messageStr = decoder.decode(message);
        if(messageStr.startsWith("HIGHLIGHT POINT"))
        {
            messageStr = messageStr.replace("HIGHLIGHT POINT ", "");
            let parts = messageStr.split(" ");
            console.log(parts);
            let point = 
            {
                x: parseFloat(parts[1]),
                y: parseFloat(parts[0])
            }

            this.callManager.dispatch(CallEvents.highlightReceived, point);
        }
    }

    AcceptCall(showVideo = true)
    {
        if(this.Status !== VideoCallStatus.INCOMING) return;

        let callMessage : CallAcceptMessage = {
            callID: this.callID,
            configuration: {
                useCamera: this.enableCam,
                facingMode: CameraFacingMode.user
            }
        }
        this.callManager._SendMessage(CallMessages.accept, callMessage);
        this.enableCam = showVideo; 
        this.Status = VideoCallStatus.CONNECTING;
    }

    RejectCall()
    {
        if(this.Status !== VideoCallStatus.INCOMING) return;
        this.callManager._SendMessage(CallMessages.end, {callID: this.callID, cause: CallEndCauses.rejected});
        this.callManager._CallEnded(CallEndStatus.rejected);
    }

    EndCall()
    {
        if(this.Status === VideoCallStatus.ENDED) return;
        this.callManager._SendMessage(CallMessages.end, {callID: this.callID, cause: CallEndCauses.userEnded});
        this.Status = VideoCallStatus.ENDED;
    }

    DeclareError()
    {
        if(this.Status === VideoCallStatus.ENDED) return;
        this.callManager._SendMessage(CallMessages.end, {callID: this.callID, cause: CallEndCauses.p2pInitFailed});
        this.callManager.dispatch(CallEvents.callError);
        this.callManager._CallEnded(CallEndStatus.error);
    }

    SendHighlight(coords : PingCoords)
    {
        let sendString = "HIGHLIGHT POINT "+ coords.y.toString()+ " "+ coords.x.toString();
        this.webRtcInstance.SendDataMessage(sendString);
    }
}

const GlobalCallsManager = new CallsManager();
export {GlobalCallsManager as CallsManager, CallEvents, VideoCallStatus as CallStatus};
